import * as Sentry from '@sentry/browser'
import { redactForInspection } from '@st/redux'
import { envVar } from './env'

// Sentry requires a DSN before it starts tracing and Replay, even when a
// tunnel owns the transport. Keep its reserved host distinct from localhost
// so Replay never mistakes application API calls for Sentry envelopes.
const LOCAL_SENTRY_DSN = 'http://qa@sentry.invalid/1'
// Canonical and worktree-local backends use different ports.
const LOCAL_API_URL = /^http:\/\/localhost:\d+/
// Account request and response bodies can contain passwords, login tokens, or
// one-time codes. Replay still records their timing and status, but not bodies.
const ACCOUNT_API_URL = /\/api\/v2\/accounts\//
const IDENTITY_VERIFICATION_API_URL = /\/api\/v2\/folders\/(startKBA|submitKBA)/

export function initSentry() {
  const localSentryTunnel = localSentryTunnelFromEnv()
  const dsn = sentryDsn(localSentryTunnel)
  // Local QA replays should show the application exactly as the tester saw it.
  // Production continues to hide input values and block captured media.
  const redactSensitiveReplayData = !import.meta.env.DEV
  const integrations: Sentry.BrowserOptions['integrations'] = [
    Sentry.browserTracingIntegration(),
    Sentry.extraErrorDataIntegration({ depth: 4, captureErrorCause: true }),
    Sentry.replayIntegration({
      blockAllMedia: redactSensitiveReplayData,
      maskAllInputs: redactSensitiveReplayData,
      maskAllText: false,
      networkRequestHeaders: ['x-request-id', 'sentry-trace', 'baggage'],
      networkCaptureBodies: true,
      networkDetailDenyUrls: [ACCOUNT_API_URL, IDENTITY_VERIFICATION_API_URL],
      networkDetailAllowUrls: [
        'https://api.stanfordtax.com',
        'https://api.stanfordtax-staging.com',
        'https://api.stanfordtax-dev.com',
        LOCAL_API_URL
      ]
    })
  ]
  if (localSentryTunnel) {
    integrations.push(Sentry.replayCanvasIntegration({ maxCanvasSize: [4096, 4096] }))
  }

  Sentry.init({
    enabled: Boolean(dsn),
    environment: sentryEnvironment(localSentryTunnel),
    dsn,
    tunnel: localSentryTunnel,
    release: envVar('NEXT_PUBLIC_BUILD_SHA'),
    tracesSampleRate: 1.0,
    // track all replays
    replaysSessionSampleRate: 1.0,
    // track all replays on error
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      'api.stanfordtax.com',
      'api.stanfordtax-staging.com',
      'api.stanfordtax-dev.com',
      LOCAL_API_URL
    ],
    ignoreErrors: [
      // This looks to be 3rd party lib, and apparently it's a harmless error
      // Ignoring this error to prevent too much noise in Sentry
      'ResizeObserver loop completed with undelivered notifications'
    ],
    integrations,
    beforeSend(event) {
      return redactForInspection(event) as typeof event
    },
    beforeBreadcrumb(breadcrumb, hint) {
      // we filter out data: and blob: urls
      // it does not help us debug and takes up space in the payload
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category == 'xhr') &&
        breadcrumb?.data?.url &&
        filterBreadcrumbURL(breadcrumb.data.url)
      ) {
        return null
      }

      return breadcrumb
    }
  })
}

function localSentryTunnelFromEnv() {
  if (!import.meta.env.DEV) return undefined
  return envVar('NEXT_PUBLIC_SENTRY_TUNNEL')
}

function sentryEnvironment(localSentryTunnel: string | undefined) {
  if (localSentryTunnel) return 'local'
  return envVar('NEXT_PUBLIC_ENV')
}

function sentryDsn(localSentryTunnel: string | undefined) {
  if (localSentryTunnel) return LOCAL_SENTRY_DSN
  return envVar('NEXT_PUBLIC_SENTRY_DSN')
}

function filterBreadcrumbURL(url: string) {
  // data and blob urls are very large and will cause sentry to choke
  // they are also unnecessary to log
  if (url.startsWith('data:')) return true
  if (url.startsWith('blob:')) return true

  return false
}
